import { Navigate } from 'react-router-dom';

import { EnumOption } from '../../types/types';

/** #CONSTANTES CENTRALIZADAS*/

export const INTERFACE_ROUTES: Record<number, string> = {
  1: '/daf/home/inicio',
  2: '/secsaude/home/inicio',
  3: '/fornecedor/home/inicio',
} as const;

export const TENANT_INTERFACE_MAP: Record<string, number> = {
  daf: 1,
  secsaude: 2,
  fornecedor: 3,
} as const;

export const INTERFACE_TENANT_MAP: Record<number, string> = {
  1: 'daf',
  2: 'secsaude',
  3: 'fornecedor',
} as const;

export const VALID_TENANTS = Object.keys(TENANT_INTERFACE_MAP) as string[];

export const DEFAULT_ROUTE = '/daf/meusdados/usuario';

/** #FUNÇÕES UTILITÁRIAS DE TOKEN */
const getToken = () => localStorage.getItem('token');

const safeParseJwtPayload = (token: string): any | null => {
  try {
    const tokenParts = token.split('.');
    if (tokenParts.length !== 3) return null;

    const base64 = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '=',
    );

    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

export const isTokenValid = () => {
  const token = getToken();
  if (!token) return false;

  const payload = safeParseJwtPayload(token);
  if (!payload || typeof payload !== 'object') return false;

  const currentTime = Math.floor(Date.now() / 1000);
  if (!payload.exp || payload.exp <= currentTime) {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    return false;
  }

  return true;
};

export const getUser = () => {
  const token = getToken();
  if (!token) return false;
  return safeParseJwtPayload(token) || false;
};

/** #FUNÇÕES DE VÍNCULO*/

export const hasVinculo = (): boolean => {
  const u: any = getUser();
  if (!u) return false;

  if (u.possuiVinculo === true) return true;
  if (Array.isArray(u.vinculo) && u.vinculo.length > 0) return true;
  if (Array.isArray(u.usuarioVinculo) && u.usuarioVinculo.length > 0)
    return true;

  return false;
};

export const getActiveVinculo = (): any | null => {
  const user: any = getUser();
  if (!user) return null;

  if (Array.isArray(user.vinculo)) {
    const vinculoAtivo = user.vinculo.find(
      (v: any) => (v?.status ?? '').toLowerCase() === 'ativo',
    );
    return vinculoAtivo || null;
  }

  return null;
};

export const determineInterfaceFromVinculo = (payload: any): number | null => {
  if (payload?.possuiVinculo && Array.isArray(payload?.vinculo)) {
    const ativo = payload.vinculo.find(
      (v: any) => v.status?.toLowerCase() === 'ativo',
    );
    if (ativo?.interface) {
      return Number(ativo.interface);
    }
  }
  return null;
};

export const getActiveInterface = (): number | null => {
  const vinculoAtivo = getActiveVinculo();
  const iface = vinculoAtivo?.interface;

  if (Number.isInteger(iface) && iface >= 1 && iface <= 3) {
    return iface;
  }

  const stored = localStorage.getItem('active_interface');
  if (!stored) return null;

  const interfaceNum = Number(stored);
  if (!Number.isInteger(interfaceNum) || interfaceNum < 1 || interfaceNum > 3) {
    return null;
  }

  return interfaceNum;
};

export const canAccessInterface = (targetInterface: number): boolean => {
  const activeInterface = getActiveInterface();

  if (!activeInterface) return false;

  const canAccess = activeInterface === targetInterface;

  if (!canAccess) {
    console.warn(
      `Acesso negado: Interface ativa é ${activeInterface}, tentou acessar ${targetInterface}`,
    );
  }

  return canAccess;
};

/** #FUNÇÕES DE ROTEAMENTO */
export const getRouteForInterface = (interfaceNum: number | null): string => {
  if (interfaceNum === null) {
    return DEFAULT_ROUTE;
  }

  return INTERFACE_ROUTES[interfaceNum] ?? DEFAULT_ROUTE;
};

export const getTenantForInterface = (
  interfaceNum: number | null,
): string | null => {
  if (interfaceNum === null) {
    return null;
  }

  return INTERFACE_TENANT_MAP[interfaceNum] ?? null;
};

export const getInterfaceForTenant = (tenant: string): number | null => {
  const interfaceNum = TENANT_INTERFACE_MAP[tenant];
  return interfaceNum ?? null;
};

export const isValidTenant = (tenant: string): boolean => {
  return tenant in TENANT_INTERFACE_MAP;
};

export const canUserAccessTenant = (tenant: string): boolean => {
  if (!isValidTenant(tenant)) {
    return false;
  }

  const targetInterface = getInterfaceForTenant(tenant);
  if (targetInterface === null) {
    return false;
  }

  return canAccessInterface(targetInterface);
};

export const recalculateInterface = (): boolean => {
  const token = localStorage.getItem('token');
  if (!token) return false;

  try {
    const payload = safeParseJwtPayload(token);
    const interfaceNum = determineInterfaceFromVinculo(payload);

    if (interfaceNum !== null) {
      localStorage.setItem('active_interface', String(interfaceNum));
      return true;
    }
  } catch (error) {
    console.error('Erro ao recalcular interface:', error);
  }

  return false;
};

export const storeActiveInterface = (interfaceNum: number | null): void => {
  if (interfaceNum) {
    localStorage.setItem('active_interface', String(interfaceNum));
  } else {
    localStorage.removeItem('active_interface');
  }
};

/** #FUNÇÕES DE PERMISSÃO*/
export const hasPermission = (
  permissions: any[],
  module: number,
  action: string,
  papelUsuarioOptions: EnumOption[],
): boolean => {
  const user: any = getUser();
  if (!user) return false;

  const vinculoAtivo = getActiveVinculo();
  const papelPrincipal = vinculoAtivo?.papel ?? null;

  const papelUsuario = papelUsuarioOptions.find(
    (option) => option.value === String(papelPrincipal),
  );

  const papeisRestritos = ['USUÁRIO', 'REPRESENTANTE TÉCNICO'];
  if (!papelUsuario) {
    return false;
  }

  if (!papeisRestritos.includes(papelUsuario.label)) {
    return true;
  }

  const modulePermissions = permissions.find((perm) => perm.modulo === module);
  if (!modulePermissions) {
    console.error(`Permissões não encontradas para o módulo: ${module}`);
    return false;
  }

  return modulePermissions[action] === 'S';
};

export const isActionAllowed = (
  module: number,
  action: string,
  papelUsuarioOptions: EnumOption[],
): boolean => {
  const user: any = getUser();
  const permissions = user?.permissoes || [];
  return hasPermission(permissions, module, action, papelUsuarioOptions);
};

/** #COMPONENTE REQUIREAUTH*/
export const RequireAuth = ({ children }: { children: JSX.Element }) => {
  if (!isTokenValid()) return <Navigate to="/" replace />;
  return children;
};

export default RequireAuth;
